// Wrestling club subscription ceiling calculator math.
//
// Pure functions, no Vue / DOM dependencies, so they can be unit tested
// without mounting a component and reused elsewhere (e.g. an internal
// dashboard). Concept: Jason Cohen's "Max MRR" applied to wrestling club
// subscriptions.
// https://longform.asmartbear.com/max-mrr/
//
// Convention: rates are passed as fractions (decimals), not percentages.
//   conversionRate 0.50 === 50%, monthlyChurnRate 0.05 === 5%.
// The Vue component is responsible for converting its percentage-based
// sliders into fractions before calling these.

// New members signed each month = leads that convert.
export function newMembersPerMonth(leads, conversionRate) {
  return leads * conversionRate;
}

// Steady-state ceiling: the point where new members exactly offset churn.
//   members_in === members_out  =>  new = max * churn  =>  max = new / churn
export function maxMembers(leads, conversionRate, monthlyChurnRate) {
  if (monthlyChurnRate <= 0) return Infinity;
  return newMembersPerMonth(leads, conversionRate) / monthlyChurnRate;
}

// Monthly recurring revenue at the ceiling.
export function maxRevenue(leads, conversionRate, monthlyChurnRate, avgRevenuePerMember) {
  return maxMembers(leads, conversionRate, monthlyChurnRate) * avgRevenuePerMember;
}

// Expected months a member stays before churning out.
export function avgMemberLifetime(monthlyChurnRate) {
  if (monthlyChurnRate <= 0) return Infinity;
  return 1 / monthlyChurnRate;
}

// Month-by-month membership trajectory starting from zero members.
//   members[t+1] = members[t] + newMembersPerMonth - (members[t] * churn)
// Returns an array of length (months + 1); members[0] === 0.
export function simulateMembers(leads, conversionRate, monthlyChurnRate, months = 60) {
  const gained = newMembersPerMonth(leads, conversionRate);
  const series = [0];
  for (let t = 0; t < months; t += 1) {
    const current = series[t];
    series.push(current + gained - current * monthlyChurnRate);
  }
  return series;
}

// Smallest month index t where members[t] >= 80% of the ceiling, starting
// from zero. Because members[t] / max === 1 - (1 - churn)^t, the answer
// depends only on churn (not on leads or conversion). Returns null if the
// ceiling is never reached within the simulated window.
export function monthsTo80Percent(monthlyChurnRate, months = 60) {
  if (monthlyChurnRate <= 0) return null;
  // Use a unit ceiling: leads/conv cancel out, so any positive values work.
  const series = simulateMembers(1, 1, monthlyChurnRate, months);
  const max = maxMembers(1, 1, monthlyChurnRate);
  const target = 0.8 * max;
  for (let t = 0; t < series.length; t += 1) {
    if (series[t] >= target) return t;
  }
  return null;
}

// Churn "zone" for color-coding and verdict copy. Thresholds are the 25th/75th
// percentiles of real monthly churn across ~89 WrestlingIQ clubs — youth
// wrestling is seasonal, so the whole band runs well above SaaS norms.
//   < 5.5%     -> healthy  (top quartile of retention)
//   5.5%-12%   -> average
//   >= 12%     -> danger   (bottom quartile)
// monthlyChurnRate is a fraction (0.08 === 8%).
export function churnZone(monthlyChurnRate) {
  const pct = monthlyChurnRate * 100;
  if (pct < 5.5) return 'healthy';
  if (pct < 12) return 'average';
  return 'danger';
}

export const HEALTHY_TARGET_CHURN = 0.055; // 5.5% — top-quartile (p25) retention; a realistic wrestling target.
